import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught React Error in Beautiful AI:", error, errorInfo);
    this.setState({ errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen w-full flex-col items-center justify-center bg-[#090a0f] p-6 text-slate-100 font-sans">
          <div className="w-full max-w-md rounded-2xl bg-[#141721] p-6 border border-white/10 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="flex size-10 items-center justify-center rounded-xl bg-red-500/20 text-red-400 text-lg">
                ⚠️
              </span>
              <div>
                <h2 className="text-base font-bold text-white">Application Exception</h2>
                <p className="text-xs text-slate-400">An unexpected error occurred during render</p>
              </div>
            </div>

            <pre className="mb-4 max-h-48 overflow-auto rounded-lg bg-[#0c0e14] p-3 text-xs text-red-300 font-mono">
              {this.state.error?.message || "Unknown error"}
              {this.state.error?.stack && `\n\n${this.state.error.stack}`}
            </pre>

            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg bg-sky-500 py-2 text-sm font-semibold text-white hover:bg-sky-400 transition-colors"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
